import { canOpenPortalJob } from '../../../utils/portalJobNavigation';
import useGuardedRealtimeList from '../../../realtime/useGuardedRealtimeList';
import useRealtimeDraft from '../../../realtime/useRealtimeDraft';
import { replaceRealtimeBookings, replaceRealtimeTerritories } from '../../../redux/slices/bookingsLogSlice';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, ButtonGroup, Card, Alert, Spinner, Collapse } from 'react-bootstrap';
import PropTypes from 'prop-types';
import NewBookingModal from './NewBookingModal';
import JobNumberAssignmentModal from './JobNumberAssignmentModal';
import InfoBubbleModal from '../../common/InfoBubbleModal';
import {
  fetchBookingsData,
  updateBookingRecord,
  setSelectedMonth,
  setSelectedYear,
  selectFilteredBookingsData,
  selectProcessedBookingsData,
  selectBookingsLoading,
  selectBookingsError,
  selectSelectedMonth,
  selectSelectedYear,
  selectCommissionSplitsByProject,
  createCommissionSplit,
  updateCommissionSplit,
  deleteCommissionSplit
} from '../../../redux/slices/bookingsLogSlice';
import { selectMappedRoles, selectUserDetails, selectUserRoles } from '../../../redux/slices/userSlice';
import { postChangeLog } from '../../../redux/slices/postChangeLogSlice';
import { fetchDropdownValues } from '../../../redux/slices/dropdownSlice';
import useUserDropdowns from '../../../functions/useUserDropdowns';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDateUnified, formatDateForForm, isDateField } from '../../../utils/dateUtils';
import { getProjectId, normalizeId } from '../../../utils/dataNormalization';
import './BookingsLog.css';

const BOOKING_FIELD_LABELS = {
  OrderDate: 'Order Date',
  State: 'State',
  ProjectName: 'Project Name',
  ProjectStatus: 'Project Status',
  Market: 'Market',
  Product: 'Product',
  Customer: 'Customer',
  Model: 'Model',
  SalesRep: 'Sales Rep',
  RepSalesPerson: 'Sales Person of Record',
  MappedSalesPerson: 'Territory Sales Person',
  Apps: 'Apps',
  InvoicePrice: 'Invoice Price',
  MTDTotal: 'MTD Total',
  YTDTotal: 'YTD Total',
  GrossMarginPercent: 'Gross Margin %',
};

const BOOKING_SUMMARY_FIELDS = new Set([
  'OrderDate',
  'ProjectName',
  'ProjectStatus',
  'Customer',
  'Model',
  'SalesRep',
  'InvoicePrice',
  'GrossMarginPercent',
]);

// Commission Splits Component - separate component for proper hook usage
const CommissionSplits = ({ job, dispatch, salesOptions, hasEditAccess, formatCurrency, userDetails, userRoles, showSaveNotificationMessage }) => {
  const commissionSplits = useSelector(state => selectCommissionSplitsByProject(state, job.ProjectID));
  
  // Get dropdown options for combined sales rep dropdown (like SalesTargets.js)
  const { options } = useSelector((state) => state.dropdown);
  
  // Create combined sales rep options (same as Sales Person of Record dropdown)
  const combinedSalesOptions = useMemo(() => {
    const additionalSalesOptions =
      options?.filter(option => option.DropdownType === "Booking Additional Sales")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

    // Only use Active Directory sales reps and Booking Additional Sales
    const salesRepsGroup = [...salesOptions];
    const additionalGroup = additionalSalesOptions.map(opt => ({
      displayName: opt.DisplayText,
      email: opt.DisplayText
    }));

    // Remove duplicates and sort alphabetically
    const uniqueSalesReps = salesRepsGroup.filter((option, index, self) => 
      index === self.findIndex(opt => opt.displayName === option.displayName)
    );
    const uniqueAdditional = additionalGroup.filter((option, index, self) => 
      index === self.findIndex(opt => opt.displayName === option.displayName)
    );
    const combined = [...uniqueSalesReps, ...uniqueAdditional];
    return combined.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [salesOptions, options]);
  
  const [newSplit, setNewSplit] = useState({
    ProjectID: job.ProjectID,
    SaleQuoteID: job.SaleQuoteID || null,
    BookingJobDetailID: job.BookingJobDetailID || null,
    SalesRep: '',
    CommissionPercentage: '',
    CommissionAmount: ''
  });
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isSavingSplit, setIsSavingSplit] = useState(false);
  const [editingSplit, setEditingSplit] = useState({ id: null, field: null });
  const [editSplitValue, setEditSplitValue] = useState('');
  useRealtimeDraft(isAddingNew || isSavingSplit || editingSplit.id !== null);
  const [currentBookingJobDetailID, setCurrentBookingJobDetailID] = useState(job.BookingJobDetailID || null);

  // Calculate totals with proper precision handling
  const totalPercentage = parseFloat(
    commissionSplits.reduce((sum, split) => 
      sum + (parseFloat(split.CommissionPercentage) || 0), 0
    ).toFixed(1)
  );
  const totalAmount = parseFloat(
    commissionSplits.reduce((sum, split) => 
      sum + (parseFloat(split.CommissionAmount) || 0), 0
    ).toFixed(2)
  );
  
  // Get invoice price from multiple sources - check both soldToShipTo and priceBreakdown tables
  // The job object already combines data from both tables
  // Priority: job.InvoicePrice (from priceBreakdown.SellPriceAmount), fallback to any other price fields
  let invoicePrice = 0;
  
  // Try different price fields that might contain the invoice price
  if (job.InvoicePrice) {
    invoicePrice = parseFloat(job.InvoicePrice);
  } else if (job.SellPriceAmount) {
    invoicePrice = parseFloat(job.SellPriceAmount);
  } else if (job.invoicePrice) {
    invoicePrice = parseFloat(job.invoicePrice);
  } else if (job.DollarValue) {
    // Fallback to DollarValue if no specific invoice price is available
    invoicePrice = parseFloat(job.DollarValue);
  }
  
  console.log('CommissionSplits - Invoice price sources:', {
    'job.InvoicePrice': job.InvoicePrice,
    'job.SellPriceAmount': job.SellPriceAmount,
    'job.invoicePrice': job.invoicePrice,
    'job.DollarValue': job.DollarValue,
    'calculated invoicePrice': invoicePrice,
    'invoicePrice type': typeof invoicePrice
  });
  const isOverPercentage = totalPercentage > 100.1; // Allow small rounding tolerance
  const isOverAmount = invoicePrice > 0 && totalAmount > (parseFloat(invoicePrice) + 0.01); // Allow 1 cent tolerance

  // Handle commission percentage change with automatic amount calculation
  const handlePercentageChange = (e) => {
    const percentage = e.target.value;
    let calculatedAmount = '';
    
    if (percentage && invoicePrice) {
      // Ensure proper decimal precision for percentage calculation
      const percentValue = parseFloat(percentage);
      const invoicePriceValue = parseFloat(invoicePrice);
      calculatedAmount = ((percentValue / 100) * invoicePriceValue).toFixed(2);
    }
    
    setNewSplit({
      ...newSplit, 
      CommissionPercentage: percentage,
      CommissionAmount: percentage && invoicePrice ? calculatedAmount : newSplit.CommissionAmount
    });
  };

  // Handle commission amount change with automatic percentage calculation
  const handleAmountChange = (e) => {
    const amount = e.target.value;
    let calculatedPercentage = '';
    
    if (amount && invoicePrice) {
      // Ensure proper decimal precision for amount calculation
      const amountValue = parseFloat(amount);
      const invoicePriceValue = parseFloat(invoicePrice);
      calculatedPercentage = ((amountValue / invoicePriceValue) * 100).toFixed(1);
    }
    
    setNewSplit({
      ...newSplit, 
      CommissionAmount: amount,
      CommissionPercentage: amount && invoicePrice ? calculatedPercentage : newSplit.CommissionPercentage
    });
  };

  // Handle add new split
  const handleAddSplit = async () => {
    if (!newSplit.SalesRep) {
      showSaveNotificationMessage('Please select a Sales Rep');
      return;
    }
    
    // Validate that at least one commission field is filled
    if (!newSplit.CommissionPercentage && !newSplit.CommissionAmount) {
      showSaveNotificationMessage('Please enter either a commission percentage or commission amount');
      return;
    }
    
    setIsSavingSplit(true);
    
    try {
      // Use the current BookingJobDetailID if available
      const splitData = {
        ...newSplit,
        BookingJobDetailID: currentBookingJobDetailID || job.BookingJobDetailID || null
      };
      
      const result = await dispatch(createCommissionSplit(splitData)).unwrap();
      
      // Log the commission split creation
      const currentUser = userDetails?.name || "Unknown User";
      const currentRole = userRoles?.[0] || "Unknown Role";
      
      const changeLogData = {
        projectID: job.ProjectID,
        currentUser,
        userRole: currentRole,
        section: 'Commission Splits',
        field: 'New Commission Split',
        prevValue: 'None',
        newValue: `${newSplit.SalesRep} - ${newSplit.CommissionPercentage || 0}% - ${formatCurrency(newSplit.CommissionAmount || 0)}`,
        dateTime: new Date().toISOString(),
      };
      
      dispatch(postChangeLog(changeLogData));
      
      // If a new BookingJobDetailID was created, update our state
      if (result.bookingJobDetailID) {
        setCurrentBookingJobDetailID(result.bookingJobDetailID);
      }
      
      // Reset form to default remaining balance values (they will be recalculated after the split is added)
      const updatedRemainingPercentage = job.RemainingCommissionPercentage || 0;
      const updatedRemainingAmount = job.RemainingCommissionAmount || 0;
      
      setNewSplit({
        ProjectID: job.ProjectID,
        SaleQuoteID: job.SaleQuoteID || null,
        BookingJobDetailID: currentBookingJobDetailID || job.BookingJobDetailID || null,
        SalesRep: '',
        CommissionPercentage: updatedRemainingPercentage,
        CommissionAmount: updatedRemainingAmount
      });
      setIsAddingNew(false);
      showSaveNotificationMessage(`Commission split created successfully for ${newSplit.SalesRep}`);
    } catch (error) {
      showSaveNotificationMessage(`Error adding commission split: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSavingSplit(false);
    }
  };

  // Handle edit split with automatic calculations
  const handleEditSplit = async () => {
    if (!editingSplit.id || !editingSplit.field) return;
    
    // Find the current split to get the previous value
    const currentSplit = commissionSplits.find(split => split.ID === editingSplit.id);
    if (!currentSplit) return;
    
    const currentValue = currentSplit[editingSplit.field];
    
    // If no change was made, just exit edit mode
    if (String(currentValue || '').trim() === String(editSplitValue || '').trim()) {
      setEditingSplit({ id: null, field: null });
      setEditSplitValue('');
      return;
    }
    
    // Prepare the update data with potential automatic calculations
    let updateData = {
      id: editingSplit.id,
      [editingSplit.field]: editSplitValue
    };

    // Only auto-calculate if we have an invoice price
    if (invoicePrice) {
      const invoicePriceValue = parseFloat(invoicePrice);
      
      // If editing percentage, also calculate and update amount
      if (editingSplit.field === 'CommissionPercentage' && editSplitValue) {
        const percentValue = parseFloat(editSplitValue);
        const calculatedAmount = ((percentValue / 100) * invoicePriceValue).toFixed(2);
        updateData.CommissionAmount = calculatedAmount;
      }
      // If editing amount, also calculate and update percentage
      else if (editingSplit.field === 'CommissionAmount' && editSplitValue) {
        const amountValue = parseFloat(editSplitValue);
        const calculatedPercentage = ((amountValue / invoicePriceValue) * 100).toFixed(1);
        updateData.CommissionPercentage = calculatedPercentage;
      }
    }
    
    try {
      await dispatch(updateCommissionSplit(updateData)).unwrap();
      
      // Log the commission split update
      const currentUser = userDetails?.name || "Unknown User";
      const currentRole = userRoles?.[0] || "Unknown Role";
      
      // Format values for display
      const formatValue = (field, value) => {
        if (field === 'CommissionPercentage') return `${value}%`;
        if (field === 'CommissionAmount') return formatCurrency(value);
        return value;
      };
      
      const changeLogData = {
        projectID: job.ProjectID,
        currentUser,
        userRole: currentRole,
        section: 'Commission Splits',
        field: `${currentSplit.SalesRep} - ${editingSplit.field}`,
        prevValue: formatValue(editingSplit.field, currentValue),
        newValue: formatValue(editingSplit.field, editSplitValue),
        dateTime: new Date().toISOString(),
      };
      
      dispatch(postChangeLog(changeLogData));
      
      setEditingSplit({ id: null, field: null });
      setEditSplitValue('');
      showSaveNotificationMessage(`Commission split updated successfully for ${currentSplit.SalesRep}`);
    } catch (error) {
      showSaveNotificationMessage(`Error updating commission split: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle delete split
  const handleDeleteSplit = async (splitId) => {
    // Find the split to get its details for logging
    const splitToDelete = commissionSplits.find(split => split.ID === splitId);
    
    if (window.confirm('Are you sure you want to delete this commission split?')) {
      try {
        await dispatch(deleteCommissionSplit(splitId)).unwrap();
        
        // Log the commission split deletion
        if (splitToDelete) {
          const currentUser = userDetails?.name || "Unknown User";
          const currentRole = userRoles?.[0] || "Unknown Role";
          
          const changeLogData = {
            projectID: job.ProjectID,
            currentUser,
            userRole: currentRole,
            section: 'Commission Splits',
            field: 'Deleted Commission Split',
            prevValue: `${splitToDelete.SalesRep} - ${splitToDelete.CommissionPercentage || 0}% - ${formatCurrency(splitToDelete.CommissionAmount || 0)}`,
            newValue: 'Deleted',
            dateTime: new Date().toISOString(),
          };
          
          dispatch(postChangeLog(changeLogData));
        }
        showSaveNotificationMessage(`Commission split deleted successfully for ${splitToDelete?.SalesRep || 'Unknown Rep'}`);
      } catch (error) {
        showSaveNotificationMessage(`Error deleting commission split: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const renderSplitCell = (split, field, value) => {
    const isEditing = editingSplit.id === split.ID && editingSplit.field === field;
    
    if (isEditing) {
      if (field === 'SalesRep') {
        return (
          <select
            value={editSplitValue}
            onChange={(e) => setEditSplitValue(e.target.value)}
            onBlur={handleEditSplit}
            onKeyDown={(e) => e.key === 'Enter' ? handleEditSplit() : e.key === 'Escape' && setEditingSplit({ id: null, field: null })}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {combinedSalesOptions.map(option => (
              <option key={option.email || option.displayName} value={option.displayName}>
                {option.displayName}
              </option>
            ))}
          </select>
        );
      } else {
        return (
          <input
            type="number"
            value={editSplitValue}
            onChange={(e) => setEditSplitValue(e.target.value)}
            onBlur={handleEditSplit}
            onKeyDown={(e) => e.key === 'Enter' ? handleEditSplit() : e.key === 'Escape' && setEditingSplit({ id: null, field: null })}
            autoFocus
            className="form-control form-control-sm"
            step={field === 'CommissionPercentage' ? '0.1' : '0.01'}
            min="0"
            max={field === 'CommissionPercentage' ? '100' : undefined}
            title={field === 'CommissionPercentage' ? 
              (invoicePrice ? `Will calculate amount: ${editSplitValue && invoicePrice ? formatCurrency((parseFloat(editSplitValue) / 100) * parseFloat(invoicePrice)) : 'N/A'}` : 'No invoice price available') :
              (invoicePrice ? `Will calculate percentage: ${editSplitValue && invoicePrice ? ((parseFloat(editSplitValue) / parseFloat(invoicePrice)) * 100).toFixed(1) + '%' : 'N/A'}` : 'No invoice price available')
            }
          />
        );
      }
    }
    
    let displayValue = value;
    if (field === 'CommissionPercentage') {
      displayValue = value ? `${value}%` : '';
    } else if (field === 'CommissionAmount') {
      displayValue = value ? formatCurrency(value) : '';
    }
    
    return (
      <span
        style={{ cursor: 'pointer' }}
        title="Double-click to edit"
        onDoubleClick={() => {
          setEditingSplit({ id: split.ID, field });
          setEditSplitValue(value || '');
        }}
      >
        {displayValue}
      </span>
    );
  };

  return (
    <div className="p-3 border-top bookings-commission-splits">
      <h6>Commission Splits</h6>
      
      {/* Validation warnings */}
      {isOverPercentage && (
        <Alert variant="warning" className="py-1 small">
          Total percentage ({totalPercentage}%) exceeds 100%
        </Alert>
      )}
      {invoicePrice > 0 && isOverAmount && (
        <Alert variant="warning" className="py-1 small">
          Total amount ({formatCurrency(totalAmount)}) exceeds invoice price ({formatCurrency(invoicePrice)})
        </Alert>
      )}
      {invoicePrice === 0 && totalAmount > 0 && (
        <Alert variant="info" className="py-1 small">
          Total amount: {formatCurrency(totalAmount)} (Invoice price not available for comparison)
        </Alert>
      )}
      
      {commissionSplits.length > 0 ? (
        <table className="table table-sm table-striped commission-splits-table">
          <thead>
            <tr>
              <th>Sales Rep</th>
              <th>
                Commission % 
                <br />
                <small className="text-muted">(auto-calculates amount)</small>
              </th>
              <th>
                Commission Amount
                <br />
                <small className="text-muted">(auto-calculates %)</small>
              </th>
              {hasEditAccess && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {commissionSplits.map(split => (
              <tr key={split.ID}>
                <td data-label="Sales Rep">{renderSplitCell(split, 'SalesRep', split.SalesRep)}</td>
                <td data-label="Commission %">{renderSplitCell(split, 'CommissionPercentage', split.CommissionPercentage)}</td>
                <td data-label="Commission Amount">{renderSplitCell(split, 'CommissionAmount', split.CommissionAmount)}</td>
                {hasEditAccess && (
                  <td data-label="Actions">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDeleteSplit(split.ID)}
                    >
                      Delete
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="fw-bold">
              <td>Total</td>
              <td className={isOverPercentage ? 'text-danger' : ''}>{totalPercentage}%</td>
              <td className={(invoicePrice > 0 && isOverAmount) ? 'text-danger' : ''}>{formatCurrency(totalAmount)}</td>
              {hasEditAccess && <td></td>}
            </tr>
          </tfoot>
        </table>
      ) : (
          <p className="text-muted mb-3">No commission splits found for this job.</p>
      )}

      {hasEditAccess && (
        <div className="mt-3">
          {!isAddingNew ? (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => {
                // Set default values using remaining balance from job data
                const remainingPercentage = job.RemainingCommissionPercentage || 0;
                const remainingAmount = job.RemainingCommissionAmount || 0;
                
                console.log('Opening new commission split form with defaults:', {
                  remainingPercentage,
                  remainingAmount,
                  jobData: job
                });
                
                setNewSplit({
                  ProjectID: job.ProjectID,
                  SaleQuoteID: job.SaleQuoteID || null,
                  BookingJobDetailID: currentBookingJobDetailID || job.BookingJobDetailID || null,
                  SalesRep: '',
                  CommissionPercentage: remainingPercentage,
                  CommissionAmount: remainingAmount
                });
                setIsAddingNew(true);
              }}
            >
              {commissionSplits.length > 0 ? 'Add Another Commission Split' : 'Add Commission Split'}
            </Button>
          ) : (
            <div className="border rounded p-3 bg-light">
              <h6 className="mb-3">Add New Commission Split</h6>
              <div className="alert alert-info py-2 mb-3 small">
                <strong>Auto-calculation:</strong> Enter either percentage or amount - the other will be calculated automatically based on invoice price of {formatCurrency(invoicePrice)}.
                {!invoicePrice && (
                  <div className="text-warning mt-1">
                    <strong>Note:</strong> No invoice price found for this job. Manual entry required.
                  </div>
                )}
              </div>
              <div className="row g-3 commission-split-form-grid">
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Sales Rep *</label>
                  <select
                    value={newSplit.SalesRep}
                    onChange={(e) => setNewSplit({...newSplit, SalesRep: e.target.value})}
                    className="form-select form-select-sm"
                    required
                  >
                    <option value="">Select Sales Rep...</option>
                    {combinedSalesOptions.map(option => (
                      <option key={option.email || option.displayName} value={option.displayName}>
                        {option.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Commission %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={newSplit.CommissionPercentage}
                    onChange={handlePercentageChange}
                    className="form-control form-control-sm"
                    placeholder="0.0"
                    title={invoicePrice ? `Invoice Price: ${formatCurrency(invoicePrice)}` : 'Invoice price not available - enter percentage manually'}
                  />
                  {invoicePrice && newSplit.CommissionPercentage && (
                    <small className="text-muted">
                      Calculated: {formatCurrency((parseFloat(newSplit.CommissionPercentage) / 100) * parseFloat(invoicePrice))}
                    </small>
                  )}
                  {!invoicePrice && newSplit.CommissionPercentage && (
                    <small className="text-warning">
                      Enter amount manually (no invoice price available)
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Commission Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newSplit.CommissionAmount}
                    onChange={handleAmountChange}
                    className="form-control form-control-sm"
                    placeholder="0.00"
                    title={invoicePrice ? `Invoice Price: ${formatCurrency(invoicePrice)}` : 'Invoice price not available - enter amount manually'}
                  />
                  {invoicePrice && newSplit.CommissionAmount && (
                    <small className="text-muted">
                      Calculated: {((parseFloat(newSplit.CommissionAmount) / parseFloat(invoicePrice)) * 100).toFixed(1)}%
                    </small>
                  )}
                  {!invoicePrice && newSplit.CommissionAmount && (
                    <small className="text-warning">
                      Enter percentage manually (no invoice price available)
                    </small>
                  )}
                </div>
              </div>
              <div className="mt-3 commission-split-form-actions">
                <Button
                  size="sm"
                  variant="success"
                  onClick={handleAddSplit}
                  disabled={isSavingSplit || !newSplit.SalesRep || (!newSplit.CommissionPercentage && !newSplit.CommissionAmount)}
                >
                  {isSavingSplit ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Commission Split'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setIsAddingNew(false);
                    // Reset to default remaining balance values
                    const remainingPercentage = job.RemainingCommissionPercentage || 0;
                    const remainingAmount = job.RemainingCommissionAmount || 0;
                    
                    setNewSplit({
                      ProjectID: job.ProjectID,
                      SaleQuoteID: job.SaleQuoteID || null,
                      BookingJobDetailID: currentBookingJobDetailID || job.BookingJobDetailID || null,
                      SalesRep: '',
                      CommissionPercentage: remainingPercentage,
                      CommissionAmount: remainingAmount
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BookingsLog = ({ showSaveNotificationMessage }) => {
  const dispatch = useDispatch();
  
  // Redux state
  const bookingsData = useSelector(selectFilteredBookingsData);
  const processedData = useSelector(selectProcessedBookingsData);
  const loading = useSelector(selectBookingsLoading);
  const error = useSelector(selectBookingsError);
  const selectedMonth = useSelector(selectSelectedMonth);
  const selectedYear = useSelector(selectSelectedYear);
  const mappedUserRoles = useSelector(selectMappedRoles);
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  
  // Permissions
  const { canEdit } = usePermissions();
  const hasEditAccess = canEdit('apps.bookings-log') || mappedUserRoles?.Admin || mappedUserRoles?.Apps;
  
  // User dropdowns for edit functionality
  const { 
    appsOptions, 
    salesOptions
  } = useUserDropdowns();
  
  // Get all commission splits from Redux
  const allCommissionSplits = useSelector(state => state.bookingsLog.data.commissionSplits);
  
  // Function to calculate total commission percentage for a job
  const calculateCommissionPercentage = useCallback((projectId) => {
    const normalizedProjectId = String(projectId);
    const jobSplits = allCommissionSplits.filter(split => 
      String(split.ProjectID) === normalizedProjectId
    );
    
    const totalPercentage = jobSplits.reduce((sum, split) => 
      sum + (parseFloat(split.CommissionPercentage) || 0), 0
    );
    
    return parseFloat(totalPercentage.toFixed(1));
  }, [allCommissionSplits]);
  
  // Function to get background color class for commission percentage
  const getCommissionBgClass = useCallback((percentage) => {
    if (percentage === 0) return 'bg-white text-dark';
    if (percentage < 100) return 'bg-warning text-dark';
    if (percentage === 100) return 'bg-success text-white';
    if (percentage > 100) return 'bg-danger text-white';
    return '';
  }, []);
  
  // Get dropdown options for combined sales rep dropdown (like SalesTargets.js)
  const { options } = useSelector((state) => state.dropdown);
  
  // Create Booking Reps only options for Sales Rep column
  const bookingRepsOnlyOptions = useMemo(() => {
    const bookingRepsOptions = 
      options?.filter(option => option.DropdownType === "Booking Reps")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

    // Use only Booking Reps (like SalesTargets.js for "Rep" type)
    const combined = bookingRepsOptions.map(opt => ({
      displayName: opt.DisplayText,
      email: opt.DisplayText
    }));
    
    // Sort alphabetically by displayName
    return combined.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [options]);
  
  // Create combined sales rep options for Sales Person of Record column
  const combinedSalesOptions = useMemo(() => {
    const additionalSalesOptions =
      options?.filter(option => option.DropdownType === "Booking Additional Sales")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

    // Only use Active Directory sales reps and Booking Additional Sales
    const salesRepsGroup = [...salesOptions];
    const additionalGroup = additionalSalesOptions.map(opt => ({
      displayName: opt.DisplayText,
      email: opt.DisplayText
    }));

    // Remove duplicates and sort alphabetically
    const uniqueSalesReps = salesRepsGroup.filter((option, index, self) => 
      index === self.findIndex(opt => opt.displayName === option.displayName)
    );
    const uniqueAdditional = additionalGroup.filter((option, index, self) => 
      index === self.findIndex(opt => opt.displayName === option.displayName)
    );
    const combined = [...uniqueSalesReps, ...uniqueAdditional];
    return combined.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [salesOptions, options]);
  
  // Local state for editing
  const [editingCell, setEditingCell] = useState({ id: null, field: null });
  const [editValue, setEditValue] = useState('');
  
  // Tab navigation state
  const [isTabNavigating, setIsTabNavigating] = useState(false);
  
  // Info bubble modal state
  const [showInfoBubbleModal, setShowInfoBubbleModal] = useState(false);
  
  // Local state for row expansion
  const [expandedRows, setExpandedRows] = useState([]);

  // Mobile/tablet cards initially show only the fields needed to scan a booking.
  const [expandedBookingDetails, setExpandedBookingDetails] = useState([]);
  
  // Search functionality state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Admin-only filter to make all fields editable
  const [adminEditAllFields, setAdminEditAllFields] = useState(false);
  
  // Toggle to hide MTD/YTD columns
  const [hideMtdYtdColumns, setHideMtdYtdColumns] = useState(false);

  // Compact layouts keep the filter controls available without occupying the page.
  const [showCompactFilters, setShowCompactFilters] = useState(false);
  
  // New booking modal state
  const [showNewBookingModal, setShowNewBookingModal] = useState(false);
  
  // Job number assignment modal state
  const [showJobNumberModal, setShowJobNumberModal] = useState(false);
  const [jobNumberModalJob, setJobNumberModalJob] = useState(null);
  useRealtimeDraft(editingCell.id !== null || showNewBookingModal || showJobNumberModal);
  useGuardedRealtimeList({ apiKey: 'bookingLogFull', params: { year: selectedYear }, blocked: loading,
    additionalResources: ['portal:general-project-info', 'portal:soldto-shipto-info', 'portal:price-breakdown-info', 'portal:booking-job-detail', 'portal:booking-commission-split'],
    apply: rows => dispatch(replaceRealtimeBookings(rows)) });
  useGuardedRealtimeList({ apiKey: 'salesTerritory', blocked: loading, apply: rows => dispatch(replaceRealtimeTerritories(rows)) });
  
  // Timeout ref for delayed row expansion
  const rowClickTimeoutRef = useRef(null);
  
  // Month names for display
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Fetch data when component mounts (only once)
  useEffect(() => {
    // Only fetch if we don't have raw processed data yet
    if (processedData.length === 0) {
      dispatch(fetchBookingsData({ year: selectedYear }));
    }
    // Fetch dropdown values for combined sales rep dropdown
    dispatch(fetchDropdownValues());
  }, [dispatch, processedData.length, selectedYear]);

  // Set to current month/year only on initial mount
  useEffect(() => {
    // Ensure we're showing current month/year on initial load (run only once)
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    
    // Only set if we haven't initialized yet (processedData is empty suggests first load)
    if (processedData.length === 0) {
      if (selectedMonth !== currentMonth) {
        dispatch(setSelectedMonth(currentMonth));
      }
      if (selectedYear !== currentYear) {
        dispatch(setSelectedYear(currentYear));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, processedData.length]); // Intentionally excluding selectedMonth and selectedYear to avoid infinite loop
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (rowClickTimeoutRef.current) {
        clearTimeout(rowClickTimeoutRef.current);
      }
    };
  }, []);
  
  // Handle month selection
  const handleMonthSelect = useCallback((month) => {
    dispatch(setSelectedMonth(month));
  }, [dispatch]);
  
  // Handle year selection  
  const handleYearSelect = useCallback((year) => {
    dispatch(setSelectedYear(year));
    dispatch(fetchBookingsData({ year }));
  }, [dispatch]);
  
  // Search filter function
  const applySearchFilter = useCallback((data, searchTerm) => {
    if (!searchTerm.trim()) return data;
    
    const lowercaseSearch = searchTerm.toLowerCase().trim();
    
    return data.filter(job => {
      // Search across multiple fields
      const searchableFields = [
        job.JobNumber,
        job.ProjectName,
        job.ProjectStatus,
        job.Customer,
        job.State,
        job.Market,
        job.Product,
        job.Model,
        job.SalesRep,
        job.RepSalesPerson,
        job.MappedSalesPerson,
        job.Apps,
        job.CustomerReqType,
        job.AuthorizedBy
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(lowercaseSearch)
      );
    });
  }, []);

  // Get filtered and searched data
  const filteredAndSearchedData = useMemo(() => {
    const searchFiltered = applySearchFilter(bookingsData, searchTerm);
    return searchFiltered;
  }, [bookingsData, searchTerm, applySearchFilter]);
  
  // Calculate total column count for colspan
  const totalColumnCount = useMemo(() => {
    // Base columns: #, Icon, %, Job Number, Order Date, State, Project Name, Project Status, Market, Product, Customer, Model, Sales Rep, Sales Person of Record, Territory Sales Person, Apps, Invoice Price, Gross Margin % = 18
    let count = 18; // Updated for new ProjectStatus column
    // Add MTD and YTD columns if not hidden
    if (!hideMtdYtdColumns) {
      count += 2; // MTD Total + YTD Total
    }
    return count;
  }, [hideMtdYtdColumns]);
  
  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);
  
  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);
  
  // Handle new booking modal
  const handleOpenNewBookingModal = useCallback(() => {
    setShowNewBookingModal(true);
  }, []);
  
  const handleCloseNewBookingModal = useCallback(() => {
    setShowNewBookingModal(false);
  }, []);
  
  const handleBookingCreated = useCallback((result) => {
    // Ensure we're viewing the current month/year to see the new booking
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    
    // Switch to current month/year if not already there
    if (selectedMonth !== currentMonth) {
      dispatch(setSelectedMonth(currentMonth));
    }
    if (selectedYear !== currentYear) {
      dispatch(setSelectedYear(currentYear));
    }
    
    // Show success notification
    showSaveNotificationMessage(`New booking created successfully! Job Number: ${result?.JobNumber || 'N/A'}`);
    
    // Refresh the bookings data after creating a new booking
    // Add a small delay to ensure backend has processed the data
    setTimeout(() => {
      dispatch(fetchBookingsData({ year: currentYear }));
    }, 1000);
  }, [dispatch, selectedMonth, selectedYear, showSaveNotificationMessage]);

  // Job number modal handlers
  const handleCloseJobNumberModal = useCallback(() => {
    setShowJobNumberModal(false);
    setJobNumberModalJob(null);
  }, []);

  const handleJobNumberSaved = useCallback((newJobNumber) => {
    showSaveNotificationMessage(`Job number updated successfully to: ${newJobNumber}`);
    // Data will be refreshed automatically by the Redux update
  }, [showSaveNotificationMessage]);
  
  // Get available years (current year plus previous and future years for bookings data)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    
    // Add years from 2 years ago to 1 year in the future (hiding older years 2020-2022)
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      years.push(year);
    }
    
    return years.reverse(); // Show newest years first (2026, 2025, 2024, etc.)
  }, []);
  
  // Handle row click for expansion with delay to prevent conflict with double-click
  const handleRowClick = useCallback((job) => {
    // Clear any existing timeout
    if (rowClickTimeoutRef.current) {
      clearTimeout(rowClickTimeoutRef.current);
      rowClickTimeoutRef.current = null;
    }
    
    // Set a delayed row expansion (250ms delay)
    rowClickTimeoutRef.current = setTimeout(() => {
      const isRowCurrentlyExpanded = expandedRows.includes(job.ProjectID);
      
      const newExpandedRows = isRowCurrentlyExpanded
        ? expandedRows.filter(id => id !== job.ProjectID)
        : [...expandedRows, job.ProjectID];
      
      setExpandedRows(newExpandedRows);
      rowClickTimeoutRef.current = null;
    }, 250); // 250ms delay to allow double-click detection
    
    // No need to fetch commission splits here - they're already loaded with the page data
  }, [expandedRows]);

  const toggleBookingDetails = useCallback((projectId) => {
    setExpandedBookingDetails(currentRows => (
      currentRows.includes(projectId)
        ? currentRows.filter(id => id !== projectId)
        : [...currentRows, projectId]
    ));
  }, []);

  const toggleCommissionSplits = useCallback((projectId) => {
    setExpandedRows(currentRows => (
      currentRows.includes(projectId)
        ? currentRows.filter(id => id !== projectId)
        : [...currentRows, projectId]
    ));
  }, []);

  // Check if a field can be edited
  const canEditField = useCallback((field, job, roles, adminEditAll = false) => {
    // Fields that are always read-only (except when admin edit all is enabled)
    const readOnlyFields = ['MappedSalesPerson', 'ProjectStatus'];
    
    // GrossMarginPercent is read-only unless admin edit all is enabled
    if (field === 'GrossMarginPercent' && !adminEditAll) {
      return false;
    }
    
    if (readOnlyFields.includes(field)) {
      return false;
    }
    
    // Admin and Apps can edit all other fields
    if (roles?.Admin || roles?.Apps) return true;
    
    // Define field permissions for other roles
    const editableFields = [
      'JobNumber',
      'OrderDate', 
      'State',
      'ProjectName',
      'Model',
      'InvoicePrice',
      'Market',
      'SalesRep',
      'RepSalesPerson',
      'Customer',
      'Product',
      'GrossMarginPercent'
    ];
    
    return editableFields.includes(field);
  }, []);

  // Handle cell double click for editing
  const handleCellDoubleClick = useCallback((job, field) => {
    if (!hasEditAccess) return;
    
    // Check if field is editable based on permissions and field type
    const isEditable = canEditField(field, job, mappedUserRoles, adminEditAllFields);
    if (!isEditable) return;
    
    // Special handling for JobNumber field - show job number assignment modal
    if (field === 'JobNumber') {
      setJobNumberModalJob(job);
      setShowJobNumberModal(true);
      return;
    }
    
    // Regular field editing for other fields
    setEditingCell({ id: job.ProjectID, field });
    setEditValue(job[field] || '');
  }, [hasEditAccess, mappedUserRoles, canEditField, adminEditAllFields]);
  
  // Handle edit save
  const handleEditSave = useCallback(async () => {
    if (!editingCell.id || !editingCell.field) return;
    
    const job = bookingsData.find(j => j.ProjectID === editingCell.id);
    if (!job) return;
    
    const table = job._fieldMappings[editingCell.field];
    if (!table) return;
    
    // Get the current value from the job data
    const currentValue = job[editingCell.field] || '';
    
    // Convert both values to strings for comparison (handles null/undefined/empty cases)
    const currentValueStr = String(currentValue).trim();
    const editValueStr = String(editValue).trim();
    
    // If no change was made, just exit edit mode without API call
    if (currentValueStr === editValueStr) {
      setEditingCell({ id: null, field: null });
      setEditValue('');
      return;
    }

    // Prepare change log data
    const currentUser = userDetails?.name || "Unknown User";
    const currentRole = userRoles?.[0] || "Unknown Role";
    
    // Format values for display in change log
    const formatValueForLog = (value) => {
      if (value === null || value === undefined || value === '') return 'Blank';
      return String(value);
    };
    
    const changeLogData = {
      projectID: job.ProjectID,
      currentUser,
      userRole: currentRole,
      section: 'Bookings Log',
      field: editingCell.field,
      prevValue: formatValueForLog(currentValue),
      newValue: formatValueForLog(editValue),
      dateTime: new Date().toISOString(),
    };
    
    try {
      // Update the booking record
      await dispatch(updateBookingRecord({
        table,
        id: job.ProjectID,
        field: editingCell.field,
        value: editValue,
        ProjectID: job.ProjectID
      })).unwrap();
      
      // Log the change
      dispatch(postChangeLog(changeLogData));
      
      setEditingCell({ id: null, field: null });
      setEditValue('');
      showSaveNotificationMessage(`Successfully updated ${editingCell.field} field`);
    } catch (error) {
      console.error('Error updating field:', error);
      showSaveNotificationMessage(`Error updating field: ${error.message || 'Unknown error'}`);
    }
  }, [editingCell, editValue, bookingsData, dispatch, userDetails, userRoles, showSaveNotificationMessage]);
  
  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setEditingCell({ id: null, field: null });
    setEditValue('');
  }, []);
  
  // Handle edit save for Tab navigation (doesn't exit edit mode or show notification immediately)
  const handleTabSave = useCallback(async () => {
    if (!editingCell.id || !editingCell.field) return false;
    
    const job = bookingsData.find(j => j.ProjectID === editingCell.id);
    if (!job) return false;
    
    const table = job._fieldMappings[editingCell.field];
    if (!table) return false;
    
    // Get the current value from the job data
    const currentValue = job[editingCell.field] || '';
    
    // Convert both values to strings for comparison (handles null/undefined/empty cases)
    const currentValueStr = String(currentValue).trim();
    const editValueStr = String(editValue).trim();
    
    // If no change was made, just return success without API call
    if (currentValueStr === editValueStr) {
      return true;
    }

    // Prepare change log data
    const currentUser = userDetails?.name || "Unknown User";
    const currentRole = userRoles?.[0] || "Unknown Role";
    
    // Format values for display in change log
    const formatValueForLog = (value) => {
      if (value === null || value === undefined || value === '') return 'Blank';
      return String(value);
    };
    
    const changeLogData = {
      projectID: job.ProjectID,
      currentUser,
      userRole: currentRole,
      section: 'Bookings Log',
      field: editingCell.field,
      prevValue: formatValueForLog(currentValue),
      newValue: formatValueForLog(editValue),
      dateTime: new Date().toISOString(),
    };
    
    try {
      // Update the booking record
      await dispatch(updateBookingRecord({
        table,
        id: job.ProjectID,
        field: editingCell.field,
        value: editValue,
        ProjectID: job.ProjectID
      })).unwrap();
      
      // Log the change
      dispatch(postChangeLog(changeLogData));
      
      // Don't exit edit mode or show notification immediately - Tab navigation will handle this
      return true;
    } catch (error) {
      console.error('Error updating field:', error);
      showSaveNotificationMessage(`Failed to update field: ${error.message || 'Unknown error'}`);
      return false;
    }
  }, [dispatch, editingCell, editValue, bookingsData, showSaveNotificationMessage, userDetails, userRoles]);
  
  // Find the next editable cell for Tab navigation
  const findNextEditableCell = useCallback((currentJobId, currentField) => {
    // Define the field order for tab navigation (editable fields only)
    const editableFieldOrder = [
      'Market', 'Customer', 'Product', 'SalesRep', 'RepSalesPerson', 'MappedSalesPerson', 'Apps',
      'GrossMarginPercent'
    ];
    
    const currentFieldIndex = editableFieldOrder.indexOf(currentField);
    
    // If not an editable field, don't navigate
    if (currentFieldIndex === -1) return null;
    
    // Find current job index
    const currentJobIndex = bookingsData.findIndex(j => j.ProjectID === currentJobId);
    if (currentJobIndex === -1) return null;
    
    const currentJob = bookingsData[currentJobIndex];
    
    // Try next field in same row
    if (currentFieldIndex < editableFieldOrder.length - 1) {
      const nextField = editableFieldOrder[currentFieldIndex + 1];
      if (canEditField(nextField, currentJob, mappedUserRoles, adminEditAllFields)) {
        return { jobId: currentJobId, field: nextField };
      }
    }
    
    // Try first field of next row
    if (currentJobIndex < bookingsData.length - 1) {
      const nextJob = bookingsData[currentJobIndex + 1];
      for (const field of editableFieldOrder) {
        if (canEditField(field, nextJob, mappedUserRoles, adminEditAllFields)) {
          return { jobId: nextJob.ProjectID, field };
        }
      }
    }
    
    return null;
  }, [bookingsData, canEditField, mappedUserRoles, adminEditAllFields]);
  
  // Handle Enter key to save, Escape to cancel, Tab for navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      setIsTabNavigating(true);
      
      // Save current value first using Tab-specific save function
      handleTabSave().then(saveSuccessful => {
        if (saveSuccessful) {
          // Find and move to next editable cell
          const nextCell = findNextEditableCell(editingCell.id, editingCell.field);
          if (nextCell) {
            // Set new editing cell
            setEditingCell({ id: nextCell.jobId, field: nextCell.field });
            
            // Get the current value for the next cell
            const targetJob = bookingsData.find(j => j.ProjectID === nextCell.jobId);
            
            if (targetJob) {
              setEditValue(targetJob[nextCell.field]?.toString() || '');
            }
            
            // Show notification for successful tab navigation after brief delay
            setTimeout(() => {
              setIsTabNavigating(false);
              if (!isTabNavigating) { // Only show if not rapidly tabbing
                showSaveNotificationMessage(`Field updated - navigated to next cell`);
              }
            }, 200);
          } else {
            // No next cell found, exit edit mode and show notification
            setEditingCell({ id: null, field: null });
            setEditValue('');
            setIsTabNavigating(false);
            showSaveNotificationMessage(`Successfully updated ${editingCell.field} value - end of table`);
          }
        } else {
          // Save failed, stay in current cell
          setIsTabNavigating(false);
        }
      });
    }
  }, [handleEditSave, handleEditCancel, findNextEditableCell, editingCell, bookingsData, handleTabSave, showSaveNotificationMessage, isTabNavigating]);
  
  // Format currency values
  const formatCurrency = useCallback((value) => {
    if (!value) return '$0.00';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numValue || 0);
  }, []);
  
  // Format percent values to 1 decimal place with % symbol
  const formatPercent = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return value;
    return `${numValue.toFixed(1)}%`;
  }, []);
  
  // Render editable cell content (without the td wrapper)
  const renderEditableCell = useCallback((job, field, value, displayValue) => {
    const isEditing = normalizeId(editingCell.id) === normalizeId(getProjectId(job)) && editingCell.field === field;
    
    if (isEditing) {
      // Handle dropdown fields with different options for different sales columns
      if (field === 'SalesRep') {
        // Sales Rep column uses only Booking Reps
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {bookingRepsOnlyOptions.map(option => (
              <option key={option.email || option.displayName} value={option.displayName}>
                {option.displayName}
              </option>
            ))}
          </select>
        );
      } else if (field === 'RepSalesPerson') {
        // Sales Person of Record uses combined list (SalesReps + Booking Additional Sales)
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {combinedSalesOptions.map(option => (
              <option key={option.email || option.displayName} value={option.displayName}>
                {option.displayName}
              </option>
            ))}
          </select>
        );
      } else if (field === 'MappedSalesPerson') {
        // Mapped Sales Person (Territory Sales Person) uses combined list
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {combinedSalesOptions.map(option => (
              <option key={option.email || option.displayName} value={option.displayName}>
                {option.displayName}
              </option>
            ))}
          </select>
        );
      } else if (field === 'Apps') {
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {appsOptions.map(option => (
              <option key={option.email} value={option.displayName}>
                {option.displayName}
              </option>
            ))}
          </select>
        );
      } else if (field === 'Market') {
        const marketOptions = options?.filter(option => option.DropdownType === "Market")
          .sort((a, b) => a.SortOrder - b.SortOrder) || [];
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {marketOptions.map(option => (
              <option key={option.ID} value={option.DisplayText}>
                {option.DisplayText}
              </option>
            ))}
          </select>
        );
      } else if (field === 'Product') {
        const productOptions = options?.filter(option => option.DropdownType === "Product")
          .sort((a, b) => a.SortOrder - b.SortOrder) || [];
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {productOptions.map(option => (
              <option key={option.ID} value={option.DisplayText}>
                {option.DisplayText}
              </option>
            ))}
          </select>
        );
      } else if (field === 'CustomerReqType') {
        const customerRequestTypeOptions = options?.filter(option => option.DropdownType === "Customer Request Type")
          .sort((a, b) => a.SortOrder - b.SortOrder) || [];
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-select form-select-sm"
          >
            <option value="">Select...</option>
            {customerRequestTypeOptions.map(option => (
              <option key={option.ID} value={option.DisplayText}>
                {option.DisplayText}
              </option>
            ))}
          </select>
        );
      } else if (isDateField(field)) {
        return (
          <input
            type="date"
            value={formatDateForForm(editValue)}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-control form-control-sm"
          />
        );
      } else {
        return (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="form-control form-control-sm"
          />
        );
      }
    }
    
    return displayValue || value || '';
  }, [
    editingCell, 
    editValue, 
    handleEditSave, 
    handleKeyDown,
    bookingRepsOnlyOptions,
    combinedSalesOptions,
    appsOptions,
    options
  ]);
  
  // Helper function to get admin edit styling
  const getAdminEditStyle = useCallback((field, job) => {
    if (!mappedUserRoles?.Admin) return {};
    // If admin edit all fields is on, show white background for all fields
    if (adminEditAllFields) return { backgroundColor: 'white' };
    // Otherwise, only show for normally editable fields
    const canEdit = canEditField(field, job, mappedUserRoles, adminEditAllFields);
    return canEdit ? {} : {};
  }, [adminEditAllFields, mappedUserRoles, canEditField]);

  // Helper function to get admin edit class
  const getAdminEditClass = useCallback((field, job) => {
    if (!mappedUserRoles?.Admin) return '';
    // If admin edit all fields is on, add class to all fields
    if (adminEditAllFields) return 'admin-editable';
    // Otherwise, only add class for normally editable fields
    const canEdit = canEditField(field, job, mappedUserRoles, adminEditAllFields);
    return canEdit ? '' : '';
  }, [adminEditAllFields, mappedUserRoles, canEditField]);

  // Render complete table cell with double-click functionality
  const renderTableCell = useCallback((job, field, value, displayValue, className = '') => {
    const canEdit = canEditField(field, job, mappedUserRoles, adminEditAllFields);
    
    // Format date fields using dateUtils for consistent display
    let formattedDisplayValue = displayValue;
    if (!formattedDisplayValue && isDateField(field) && value) {
      formattedDisplayValue = formatDateUnified(value, 'display');
    } else if (!formattedDisplayValue) {
      formattedDisplayValue = value;
    }
    
    // For Product field, display only first letter when not editing (like Engineers in JobScheduleTable)
    if (field === 'Product' && formattedDisplayValue) {
      formattedDisplayValue = formattedDisplayValue.charAt(0);
    }
    
    // Add admin edit mode styling - white background for editable fields when admin mode is on
    let cellStyle = { cursor: canEdit ? 'pointer' : 'default' };
    let cellClassName = `${className} ${canEdit ? 'editable-cell' : ''}`.trim();
    
    // Add conditional styling for ProjectStatus = "Canceled" or "Calcelled"
    if (field === 'ProjectStatus' && value) {
      const lowerValue = value.toString().toLowerCase();
      if (lowerValue === 'canceled' || lowerValue === 'cancelled') {
        cellStyle = { ...cellStyle, backgroundColor: 'red', color: 'white' };
      }
    }
    
    if (adminEditAllFields && mappedUserRoles?.Admin && canEdit) {
      // Only override admin styling if this isn't a canceled/calcelled ProjectStatus
      if (field === 'ProjectStatus' && value) {
        const lowerValue = value.toString().toLowerCase();
        if (!(lowerValue === 'canceled' || lowerValue === 'cancelled')) {
          cellStyle = { ...cellStyle, backgroundColor: 'white' };
        }
      } else {
        cellStyle = { ...cellStyle, backgroundColor: 'white' };
      }
      cellClassName += ' admin-editable';
    }
    
    return (
      <td
        data-field={field}
        data-label={BOOKING_FIELD_LABELS[field] || field}
        data-compact={BOOKING_SUMMARY_FIELDS.has(field) ? 'summary' : 'detail'}
        className={cellClassName}
        onDoubleClick={(e) => {
          if (canEdit) {
            e.stopPropagation(); // Prevent row click when double-clicking to edit
            
            // Clear the row click timeout to prevent row expansion
            if (rowClickTimeoutRef.current) {
              clearTimeout(rowClickTimeoutRef.current);
              rowClickTimeoutRef.current = null;
            }
            
            handleCellDoubleClick(job, field);
          }
        }}
        title={canEdit ? 'Double-click to edit' : ''}
        style={cellStyle}
      >
        {renderEditableCell(job, field, value, formattedDisplayValue)}
      </td>
    );
  }, [
    canEditField,
    mappedUserRoles,
    handleCellDoubleClick,
    renderEditableCell,
    rowClickTimeoutRef,
    adminEditAllFields
  ]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="danger">
        Error loading bookings data: {error}
      </Alert>
    );
  }
  
  return (
    <div className="bookings-log-container">
      <Card>
        <Card.Header className="bookings-log-card-header">
          <div className="bookings-log-heading-actions">
            <h5 className="mb-0">
              Bookings Log
              <i 
                className="bi bi-info-circle ms-2 text-primary" 
                style={{ cursor: 'pointer', fontSize: '1rem' }}
                onClick={() => setShowInfoBubbleModal(true)}
                title="Click for page information"
              ></i>
            </h5>
            <div className="bookings-log-header-buttons">
              {hasEditAccess && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleOpenNewBookingModal}
                  title="Create a new booking item"
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Add New Booking
                </Button>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                className="bookings-log-compact-filter-toggle"
                onClick={() => setShowCompactFilters(filtersVisible => !filtersVisible)}
                aria-expanded={showCompactFilters}
                aria-controls="bookings-log-compact-filters"
              >
                <i className="bi bi-funnel me-1"></i>
                Filters: {selectedMonth === 0 ? 'All months' : monthNames[selectedMonth - 1]} {selectedYear}
                <i className={`bi ${showCompactFilters ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`}></i>
              </Button>
            </div>
          </div>

          <div
            id="bookings-log-compact-filters"
            className={`bookings-log-filter-panel ${showCompactFilters ? 'is-open' : ''}`}
          >
            <div className="bookings-log-filter-controls">
            {/* Toggle to hide MTD/YTD columns */}
            <div className="bookings-log-view-toggle">
              <label className="form-label m-1 small fw-bold">View:</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="hideMtdYtdColumns"
                  checked={hideMtdYtdColumns}
                  onChange={(e) => setHideMtdYtdColumns(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="hideMtdYtdColumns">
                  Hide MTD/YTD
                </label>
              </div>
            </div>
            
            {/* Admin-only filter to make all fields editable */}
            {mappedUserRoles?.Admin && (
              <div className="bookings-log-admin-toggle">
                <label className="form-label m-1 small fw-bold text-warning">Admin:</label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="adminEditAllFields"
                    checked={adminEditAllFields}
                    onChange={(e) => setAdminEditAllFields(e.target.checked)}
                  />
                  <label className="form-check-label small text-warning" htmlFor="adminEditAllFields">
                    Edit All Fields
                  </label>
                </div>
              </div>
            )}
            
            {/* Year Selection */}
            <div className="bookings-log-year-filter">
              <label className="form-label m-1 small fw-bold">Year:</label>
              <select
                className="form-select bookings-log-compact-select"
                aria-label="Select bookings year"
                value={selectedYear}
                onChange={(event) => handleYearSelect(Number(event.target.value))}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <ButtonGroup size="sm">
                {availableYears.map(year => (
                  <Button
                    key={year}
                    variant={selectedYear === year ? 'primary' : 'outline-primary'}
                    onClick={() => handleYearSelect(year)}
                    title={`View bookings for ${year}`}
                  >
                    {year}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
            
            {/* Month Selection */}
            <div className="bookings-log-month-filter">
              <label className="form-label m-1 small">Month:</label>
              <select
                className="form-select bookings-log-compact-select"
                aria-label="Select bookings month"
                value={selectedMonth}
                onChange={(event) => handleMonthSelect(Number(event.target.value))}
              >
                <option value={0}>All months</option>
                {monthNames.map((month, index) => (
                  <option key={month} value={index + 1}>{month}</option>
                ))}
              </select>
              <ButtonGroup size="sm">
                {/* All Months Button */}
                <Button
                  key="all"
                  variant={selectedMonth === 0 ? 'primary' : 'outline-primary'}
                  onClick={() => handleMonthSelect(0)}
                  title="View all months for the selected year"
                >
                  All
                </Button>
                {monthNames.map((month, index) => (
                  <Button
                    key={month}
                    variant={selectedMonth === index + 1 ? 'primary' : 'outline-primary'}
                    onClick={() => handleMonthSelect(index + 1)}
                  >
                    {month.substring(0, 3)}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
            </div>
          </div>
        </Card.Header>
        
        {/* Search Bar and Total Count */}
        <div className="border-bottom p-3 bookings-log-search-row">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by job number, project name, customer, sales rep, etc..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-6 text-end bookings-log-result-count">
              <span className="text-muted">
                Showing <strong>{filteredAndSearchedData.length}</strong> 
                {searchTerm ? ' of ' + bookingsData.length : ''} items
                {searchTerm && (
                  <span className="text-info ms-2">
                    (filtered by: "{searchTerm}")
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        
        <Card.Body className="p-0">
          <div className="bookings-table-container">
            <table className="table table-sm table-striped table-hover mb-0">
              <thead className="table-dark sticky-top">
                <tr>
                  <th style={{width: '50px', minWidth: '50px', maxWidth: '50px'}}>#</th>
                  <th style={{width: '40px', minWidth: '40px', maxWidth: '40px'}}></th>
                  <th style={{width: '60px', minWidth: '60px', maxWidth: '60px'}}>%</th>
                  <th style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>Job Number</th>
                  <th style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>Order Date</th>
                  <th style={{width: '80px', minWidth: '80px', maxWidth: '80px'}}>State</th>
                  <th style={{width: '300px', minWidth: '250px', maxWidth: '300px'}}>Project Name</th>
                  <th style={{width: '120px', minWidth: '120px', maxWidth: '120px'}}>Project Status</th>
                  <th>Market</th>
                  <th>Product</th>
                  <th>Customer</th>
                  <th>Model</th>
                  <th>Sales Rep</th>
                  <th>Sales Person of Record</th>
                  <th>Territory Sales Person</th>
                  <th>Apps</th>
                  <th>Invoice Price</th>
                  {!hideMtdYtdColumns && <th>MTD Total</th>}
                  {!hideMtdYtdColumns && <th>YTD Total</th>}
                  <th>Gross Margin %</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSearchedData.length === 0 ? (
                  <tr>
                    <td colSpan={totalColumnCount} className="text-center text-muted py-4 bookings-empty-row">
                      {searchTerm 
                        ? `No bookings found matching "${searchTerm}" for ${selectedMonth === 0 ? 'all months of ' : monthNames[selectedMonth - 1] + ' '}${selectedYear}`
                        : `No bookings data found for ${selectedMonth === 0 ? 'all months of ' : monthNames[selectedMonth - 1] + ' '}${selectedYear}`
                      }
                    </td>
                  </tr>
                ) : (
                  filteredAndSearchedData.map((job, index) => (
                    <React.Fragment key={job.ProjectID}>
                      <tr 
                        className={`bookings-record-row ${expandedRows.includes(job.ProjectID) ? 'table-active' : ''} ${expandedBookingDetails.includes(job.ProjectID) ? 'bookings-record-details-expanded' : ''}`}
                      >
                        <td data-field="Row" data-label="Record" className="text-muted small">{index + 1}</td>
                        <td data-field="Portal" data-label="Open Job" className="text-center" style={{ width: '40px' }}>
                          {canOpenPortalJob(job) && (job.BookingLogOnly === false || job.BookingLogOnly === null) && (
                            <a 
                              className="bookings-portal-icon"
                              href={`/${job.ProjectID}/generalInfo`}
                              target="_self"
                              rel="noopener noreferrer"
                              title="Open in Production Portal"
                              style={{ color: '#6c757d', textDecoration: 'none' }}
                            >
                              <i className="bi bi-file-earmark"></i>
                            </a>
                          )}
                          <div className="bookings-compact-actions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => toggleBookingDetails(job.ProjectID)}
                              aria-expanded={expandedBookingDetails.includes(job.ProjectID)}
                            >
                              <i className={`bi ${expandedBookingDetails.includes(job.ProjectID) ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                              {expandedBookingDetails.includes(job.ProjectID) ? 'Less details' : 'More details'}
                            </Button>
                            <Button
                              variant={expandedRows.includes(job.ProjectID) ? 'primary' : 'outline-primary'}
                              size="sm"
                              onClick={() => toggleCommissionSplits(job.ProjectID)}
                              aria-expanded={expandedRows.includes(job.ProjectID)}
                            >
                              <i className="bi bi-percent me-1"></i>
                              Commission splits
                            </Button>
                            {canOpenPortalJob(job) && (job.BookingLogOnly === false || job.BookingLogOnly === null) && (
                              <Button
                                as="a"
                                variant="outline-secondary"
                                size="sm"
                                href={`/${job.ProjectID}/generalInfo`}
                              >
                                <i className="bi bi-box-arrow-up-right me-1"></i>
                                Open job
                              </Button>
                            )}
                          </div>
                        </td>
                        <td 
                          data-field="CommissionPercentage"
                          data-label="Commission %"
                          data-compact="summary"
                          className={`text-center ${getCommissionBgClass(calculateCommissionPercentage(job.ProjectID))} ${getAdminEditClass('CommissionPercentage', job)}`}
                          title="Commission splits percentage total - Click to expand/collapse"
                          style={{ 
                            cursor: 'pointer',
                            ...getAdminEditStyle('CommissionPercentage', job)
                          }}
                          onClick={() => handleRowClick(job)}
                        >
                          {calculateCommissionPercentage(job.ProjectID)}%
                        </td>
                        <td 
                          data-field="JobNumber"
                          data-label="Job Number"
                          data-compact="summary"
                          className={`${getAdminEditClass('JobNumber', job)} ${canEditField('JobNumber', job, mappedUserRoles, adminEditAllFields) ? 'editable-cell' : ''}`}
                          style={{ 
                            ...getAdminEditStyle('JobNumber', job)
                          }}
                          onDoubleClick={(e) => {
                            if (canEditField('JobNumber', job, mappedUserRoles, adminEditAllFields)) {
                              e.stopPropagation();
                              if (rowClickTimeoutRef.current) {
                                clearTimeout(rowClickTimeoutRef.current);
                                rowClickTimeoutRef.current = null;
                              }
                              handleCellDoubleClick(job, 'JobNumber');
                            }
                          }}
                          title={canEditField('JobNumber', job, mappedUserRoles, adminEditAllFields) ? "Double-click to edit" : ""}
                        >
                          {canEditField('JobNumber', job, mappedUserRoles, adminEditAllFields) && normalizeId(editingCell.id) === normalizeId(getProjectId(job)) && editingCell.field === 'JobNumber'
                            ? renderEditableCell(job, 'JobNumber', job.JobNumber)
                            : job.JobNumber
                          }
                        </td>
                        {renderTableCell(job, 'OrderDate', job.OrderDate, formatDateUnified(job.OrderDate, 'display'))}
                        {renderTableCell(job, 'State', job.State)}
                        {renderTableCell(job, 'ProjectName', job.ProjectName)}
                        {renderTableCell(job, 'ProjectStatus', job.ProjectStatus)}
                        {renderTableCell(job, 'Market', job.Market)}
                        {renderTableCell(job, 'Product', job.Product)}
                        {renderTableCell(job, 'Customer', job.Customer)}
                        {renderTableCell(job, 'Model', job.Model)}
                        {renderTableCell(job, 'SalesRep', job.SalesRep)}
                        {renderTableCell(job, 'RepSalesPerson', job.RepSalesPerson)}
                        {renderTableCell(job, 'MappedSalesPerson', job.MappedSalesPerson)}
                        {renderTableCell(job, 'Apps', job.Apps)}
                        {renderTableCell(job, 'InvoicePrice', job.InvoicePrice, formatCurrency(job.InvoicePrice))}
                        {!hideMtdYtdColumns && (
                          <td 
                            data-field="MTDTotal"
                            data-label="MTD Total"
                            data-compact="detail"
                            className={getAdminEditClass('MTDTotal', job)}
                            style={getAdminEditStyle('MTDTotal', job)}
                          >
                            {formatCurrency(job.MTDTotal)}
                          </td>
                        )}
                        {!hideMtdYtdColumns && (
                          <td 
                            data-field="YTDTotal"
                            data-label="YTD Total"
                            data-compact="detail"
                            className={getAdminEditClass('YTDTotal', job)}
                            style={getAdminEditStyle('YTDTotal', job)}
                          >
                            {formatCurrency(job.YTDTotal)}
                          </td>
                        )}
                        {renderTableCell(job, 'GrossMarginPercent', job.GrossMarginPercent, formatPercent(job.GrossMarginPercent))}
                      </tr>
                      {/* Expandable row for commission splits */}
                      <tr className="bookings-detail-row">
                        <td colSpan={totalColumnCount} className="p-0">
                          <Collapse in={expandedRows.includes(job.ProjectID)}>
                            <div>
                              <CommissionSplits 
                                job={job} 
                                dispatch={dispatch}
                                salesOptions={salesOptions}
                                hasEditAccess={hasEditAccess}
                                formatCurrency={formatCurrency}
                                userDetails={userDetails}
                                userRoles={userRoles}
                                showSaveNotificationMessage={showSaveNotificationMessage}
                              />
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      
      {/* New Booking Modal */}
      <NewBookingModal 
        show={showNewBookingModal}
        handleClose={handleCloseNewBookingModal}
        onBookingCreated={handleBookingCreated}
      />

      {/* Job Number Assignment Modal */}
      <JobNumberAssignmentModal
        show={showJobNumberModal}
        onHide={handleCloseJobNumberModal}
        job={jobNumberModalJob}
        onSave={handleJobNumberSaved}
      />

      {/* Info Bubble Modal */}
      <InfoBubbleModal
        show={showInfoBubbleModal}
        onHide={() => setShowInfoBubbleModal(false)}
        sectionName="Booking Log"
        value="Page"
        title="Bookings Log Information"
        displayAsOrderedList={true}
      />
    </div>
  );
};

BookingsLog.propTypes = {
  showSaveNotificationMessage: PropTypes.func.isRequired,
};

export default BookingsLog;
